import React, { useState, useEffect } from "react";
import {
  ChevronDown,
  Search,
  Mail,
  ChevronLeft,
  ChevronRight,
  LogOut,
} from "lucide-react";
import Swal from "sweetalert2";
import "../Styles/attendees.css";

const API_BASE_URL = "";

export default function AttendeesDashboard({ selectedEvent = "confluence" }) {
  const eventParam = selectedEvent === "festival" ? "festival" : "confluence";
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const [sendingId, setSendingId] = useState(null);
  const [sendingAll, setSendingAll] = useState(false);

  const fetchAttendees = async () => {
    try {
      setLoading(true);
            const res = await fetch(`${API_BASE_URL}/api/attendees?event=${eventParam}`);
      if (!res.ok) throw new Error("Failed to load attendees");
      const data = await res.json();
      setAttendees(data);
      setError(null);
    } catch (err) {
      console.error(err);
      setError("Could not load attendees. Is the backend running?");
      setAttendees([]);
    } finally {
      setLoading(false);
    }
  };

    useEffect(() => {
    setAttendees([]);
    setSelected([]);
    setQuery("");
    fetchAttendees();
  }, [eventParam]);

  const filteredAttendees = attendees.filter((a) =>
    a.fullName?.toLowerCase().includes(query.toLowerCase())
  );

  // Reset to page 1 whenever the search term or page size changes,
  // so we never end up "stuck" on a page that no longer exists.
  useEffect(() => {
    setCurrentPage(1);
  }, [query, entriesPerPage]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredAttendees.length / entriesPerPage)
  );

  // Only the rows visible on the current page
  const pageAttendees = filteredAttendees.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  // "Select All" only affects the ids currently visible on this page.
  // Selections made on other pages are preserved.
  const toggleAll = () => {
    const pageIds = pageAttendees.map((a) => a.id);
    const allPageSelected =
      pageIds.length > 0 && pageIds.every((id) => selected.includes(id));

    if (allPageSelected) {
      setSelected((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelected((prev) => [...new Set([...prev, ...pageIds])]);
    }
  };

  const toggleOne = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const isPageFullySelected =
    pageAttendees.length > 0 &&
    pageAttendees.every((a) => selected.includes(a.id));

  const handleSendCertificate = async (id) => {
    setSendingId(id);
    try {
      const res = await fetch(`${API_BASE_URL}/api/attendees/${id}/send-certificate`, {
        method: "POST",
      });
      if (!res.ok) throw new Error("Failed to send certificate");
      await fetchAttendees();

      Swal.fire({
        icon: "success",
        title: "Certificate Sent!",
        text: "The certificate has been emailed successfully.",
        confirmButtonColor: "#2f6fed",
      });
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Failed to Send",
        text: "Could not send the certificate. Please try again.",
        confirmButtonColor: "#e0432c",
      });
    } finally {
      setSendingId(null);
    }
  };

  // Sends certificates only to attendees on the CURRENT page who
  // don't already have one sent. The backend processes these one-by-one
  // in the background (with a delay between each email), so after
  // kicking off the send we POLL the attendees list every 2 seconds
  // until every targeted id shows certificateSent = true. This is what
  // makes the status pill and button flip live for each person as their
  // email actually goes out, instead of the UI looking frozen.
  const handleSendCertificateToAll = async () => {
    const idsOnPage = pageAttendees
      .filter((a) => !a.certificateSent)
      .map((a) => a.id);

    if (idsOnPage.length === 0) {
      Swal.fire({
        icon: "info",
        title: "Nothing to Send",
        text: "Everyone on this page already has a certificate.",
        confirmButtonColor: "#2f6fed",
      });
      return;
    }

    const result = await Swal.fire({
      icon: "question",
      title: "Send Certificates?",
      text: `Send certificates to ${idsOnPage.length} attendee(s) on this page?`,
      showCancelButton: true,
      confirmButtonText: "Yes, send it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#3D44B1",
      cancelButtonColor: "#6b7086",
    });

    if (!result.isConfirmed) return;

    setSendingAll(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/attendees/send-certificate-all`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: idsOnPage }),
      });
      if (!res.ok) throw new Error("Failed to send certificates");

      // Uncheck these rows immediately so "Select All" doesn't stay stuck
      setSelected((prev) => prev.filter((id) => !idsOnPage.includes(id)));

      Swal.fire({
        icon: "success",
        title: "Bulk Send Started!",
        text: `Sending certificates to ${idsOnPage.length} attendee(s). Statuses will update as each email goes out.`,
        confirmButtonColor: "#2f6fed",
      });

      // Poll until every id in this batch is marked sent, or we hit
      // a safety cap (covers ~3 minutes at 2s per attendee on the backend).
      const maxAttempts = 90;
      let attempts = 0;

      const poll = async () => {
        attempts++;
        try {
                    const r = await fetch(`${API_BASE_URL}/api/attendees?event=${eventParam}`);
          if (!r.ok) throw new Error("Failed to refresh attendees");
          const data = await r.json();
          setAttendees(data);

          const stillPending = data.some(
            (a) => idsOnPage.includes(a.id) && !a.certificateSent
          );

          if (stillPending && attempts < maxAttempts) {
            setTimeout(poll, 2000);
          } else {
            setSendingAll(false);
          }
        } catch (err) {
          console.error("Polling error:", err);
          setSendingAll(false);
        }
      };

      poll();
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Failed to Send",
        text: "Could not start sending certificates. Please try again.",
        confirmButtonColor: "#e0432c",
      });
      setSendingAll(false);
    }
  };

  const total = attendees.length;
  const certificateSentCount = attendees.filter((a) => a.certificateSent).length;
  const unsentCount = total - certificateSentCount;

  const formatCheckInTime = (dateValue) => {
    if (!dateValue) return "-";

    const date = new Date(dateValue);

    if (isNaN(date.getTime())) return "-";

    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(date);

    const values = {};
    parts.forEach(({ type, value }) => {
      values[type] = value;
    });

    return `${values.year}-${values.month}-${values.day} ${values.hour}:${values.minute}:${values.second}`;
  };

  // Page numbers with "…" (same as the other list pages)
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    if (start > 2) pages.push("dots-start");
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1) pages.push("dots-end");
    pages.push(totalPages);
    return pages;
  };

  return (
    <div className="atd-app atd-app--no-sidebar">
      <main className="atd-main">
        <section className="atd-panel">
          <div className="atd-stats">
            <label className="atd-pill atd-pill--entries">
              Show Entries :
              <select
                value={entriesPerPage}
                onChange={(e) => setEntriesPerPage(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </label>

            <button
              className="atd-pill atd-pill--action"
              onClick={handleSendCertificateToAll}
              disabled={sendingAll}
            >
              <Mail size={14} />
              {sendingAll ? "Sending..." : "Certificates Sent to All"}
            </button>

            <span className="atd-pill atd-pill--outline atd-pill--amber-outline">
              Total Attendees : {total}
            </span>
            <span className="atd-pill atd-pill--outline atd-pill--green-outline">
              Certificate Sent : {certificateSentCount}
            </span>
            <span className="atd-pill atd-pill--outline atd-pill--red-outline">
              Unsent : {unsentCount}
            </span>

            <div className="atd-search">
              <Search size={14} />
              <input
                placeholder="Search..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="atd-table-wrap">
            {loading ? (
              <p className="atd-state-msg">Loading attendees...</p>
            ) : error ? (
              <p className="atd-state-msg atd-state-msg--error">{error}</p>
            ) : (
              <table className="atd-table">
                <thead>
                  <tr>
                    <th className="atd-checkbox-col">
                      <input
                        type="checkbox"
                        checked={isPageFullySelected}
                        onChange={toggleAll}
                      />
                      <span>Select All</span>
                    </th>
                    <th>S.No</th>
                    <th>Name</th>
                    <th>Designation</th>
                    <th>Company</th>
                    <th>Contact</th>
                    <th>Personal Email</th>
                    <th>Official Email</th>
                    <th>Check-In Time</th>
                    <th>Certificate Status</th>
                    <th>Certificate Sent</th>
                  </tr>
                </thead>
                <tbody>
                  {pageAttendees.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="atd-state-msg">
                        No attendees yet.
                      </td>
                    </tr>
                  ) : (
                    pageAttendees.map((a, index) => (
                      <tr key={a.id}>
                        <td className="atd-checkbox-col">
                          <input
                            type="checkbox"
                            checked={selected.includes(a.id)}
                            onChange={() => toggleOne(a.id)}
                          />
                        </td>
                        <td>{(currentPage - 1) * entriesPerPage + index + 1}</td>
                        <td>
                          <div className="atd-user-cell">
                            <span className="atd-avatar">{a.fullName?.charAt(0)}</span>
                            {a.fullName}
                          </div>
                        </td>
                        <td>{a.designation}</td>
                        <td>{a.company}</td>
                        <td>{a.contact}</td>
                        <td>{a.personalEmail}</td>
                        <td>{a.officialEmail}</td>
                        <td>{formatCheckInTime(a.checkInTime)}</td>
                        <td>
                          <span
                            className={`atd-status-pill ${
                              a.certificateSent
                                ? "atd-status-pill--sent"
                                : "atd-status-pill--unsent"
                            }`}
                          >
                            {a.certificateSent ? "✓ Sent" : "✕ UnSent"}
                          </span>
                        </td>
                        <td>
                          <button
                            className={`atd-send-cert-btn ${
                              a.certificateSent ? "atd-send-cert-btn--sent" : ""
                            }`}
                            disabled={a.certificateSent || sendingId === a.id}
                            onClick={() => handleSendCertificate(a.id)}
                          >
                            {sendingId === a.id ? "Sending..." : "Sent Certificate"}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>

          <div className="atd-table-footer">
            <span>
              Showing {pageAttendees.length === 0 ? 0 : (currentPage - 1) * entriesPerPage + 1}
              &ndash;
              {(currentPage - 1) * entriesPerPage + pageAttendees.length} of {filteredAttendees.length} Attendees
            </span>
            <div className="atd-pagination">
              <button
                className="atd-page-btn"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={14} />
              </button>

              {getPageNumbers().map((p, i) =>
                p === "dots-start" || p === "dots-end" ? (
                  <span key={`${p}-${i}`} className="atd-page-dots">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    className={`atd-page-btn${p === currentPage ? " active" : ""}`}
                    onClick={() => setCurrentPage(p)}
                  >
                    {p}
                  </button>
                )
              )}

              <button
                className="atd-page-btn"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}